import { GAME } from '@continuum/show-core';
import type { Lang } from './text.js';

/** "A and B", "A, B and C". */
function andList(names: readonly string[], and: string): string {
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} ${and} ${names[names.length - 1]}`;
}

/**
 * The saboteur game, in plain words.
 *
 * Guests read this with a glass in one hand, many of them in a second language, and none of
 * them came to learn a game. So: everyday words (saboteur, alarm, fix, damage, voted out),
 * short sentences, one idea a card. The code underneath still says anomaly, fracture and brig;
 * nobody at the table ever has to.
 */
export interface RuleLine {
  /** The mark beside the line, drawn as the table draws it (Game.tsx `RuleMark`). */
  mark: 'film' | 'phone' | 'ring' | 'stripes' | 'both' | 'vote';
  text: string;
}

export interface RuleCard {
  title: string;
  /** The table as the projector draws it (Game.tsx `RULE_PICTURES`), named so it stays with its card. */
  art: 'play' | 'fix' | 'stripes';
  /** What the picture says, for a screen reader. */
  alt: string;
  /** One short line a mark, read beside the picture. */
  lines: readonly RuleLine[];
}

export interface GameCopy {
  private: string;
  continue: string;
  seat: string;
  you: string;
  volunteerEyebrow: string;
  volunteerTitle: string;
  volunteerLede: string;
  /** For a guest checking in after launch, when the saboteurs are already picked. */
  volunteerLedeLate: string;
  volunteerYes: string;
  volunteerYesSub: string;
  volunteerNo: string;
  volunteerNoSub: string;
  volunteerNote: string;
  rulesEyebrow: string;
  rules: readonly RuleCard[];
  back: string;
  next: string;
  takeSeat: string;
  timeline: string;
  controls: string;
  close: string;
  /** Private notes, on the saboteur's own game only (games/Hack.tsx), when the room turns a sabotage down. */
  sabotageBusy: string;
  sabotageTooSoon: string;
  sabotageNoneLeft: string;
  sabotageNotNow: string;
  sabotageOver: string;
  anomaly: string;
  /** Which of the stop's alarms this is, beside ALARM. */
  alarmOf: (n: number, of: number) => string;
  near: (from: number, to: number) => string;
  /** The same, short enough for the alarm bar. */
  fixersShort: (names: readonly string[]) => string;
  stabilised: string;
  fractured: (damage: number, of: number) => string;
  meeting: string;
  /** On the meeting bar, before this phone has voted. */
  voteNow: string;
  calledBy: (name: string) => string;
  calledBySteward: string;
  meetingPrompt: string;
  suspected: (name: string) => string;
  voteYes: string;
  voteNo: string;
  youAreSuspect: string;
  yes: string;
  no: string;
  yourVote: (option: string) => string;
  voted: (n: number, of: number) => string;
  noVoteInBrig: string;
  toBrig: (name: string, agent: boolean) => string;
  nobodyToBrig: string;
  uprisingTitle: string;
  uprisingBody: string;
  tapToOpen: string;
  hide: string;
  /** CONTROLS: the role card again. On every phone, so it gives nothing away. */
  myRole: string;
  /** The toolbar chip, and what it says when tapped. */
  votedOut: string;
  votedOutExplain: string;
  /** What DAMAGE on the toolbar means, when tapped. */
  damageExplain: (n: number, of: number) => string;
}

const en: GameCopy = {
  private: 'PRIVATE — HIDE YOUR SCREEN',
  continue: 'CONTINUE',
  seat: 'SEAT',
  you: 'YOU',
  volunteerEyebrow: 'CHECK-IN · 2 / 3',
  volunteerTitle: 'WANT TO BE A SECRET SABOTEUR?',
  volunteerLede:
    'At every stop, up to two guests are secretly picked to sabotage the ship. Say yes, and it could be you. Your phone tells you, privately.',
  volunteerLedeLate:
    'This stop’s saboteurs are already picked. Say yes, and it could be you at the next stop. Your phone tells you, privately.',
  volunteerYes: "YES, I'M IN",
  volunteerYesSub: 'Sounds fun',
  volunteerNo: 'NO THANKS',
  volunteerNoSub: 'I’d rather be crew',
  volunteerNote: 'Only you and the captain will see your answer.',
  rulesEyebrow: 'CHECK-IN · 3 / 3 · HOW TO PLAY',
  // Three cards, each the table as it will look and what to watch for on it (Marek, 2026-10-06:
  // shorter, easier, with pictures). The rest is explained where it happens.
  rules: [
    {
      title: 'YOUR PHONE IS YOUR CONSOLE',
      art: 'play',
      alt: 'Your place on the table, with PLAY NOW in front of you.',
      lines: [
        { mark: 'film', text: 'Film on: phones down, watch the big screen.' },
        { mark: 'phone', text: 'PLAY NOW at your place: play here.' },
      ],
    },
    {
      title: 'WHITE CIRCLE = YOU FIX',
      art: 'fix',
      alt: 'A white circle on the table in front of a guest.',
      lines: [
        { mark: 'ring', text: 'A white circle at your place: you fix the alarm.' },
        { mark: 'phone', text: 'Solve the puzzle on your phone, fast.' },
      ],
    },
    {
      title: 'STRIPES = SUSPECTS',
      art: 'stripes',
      alt: 'Four places in yellow and black stripes; one of them also has a white circle.',
      lines: [
        { mark: 'stripes', text: 'The alarm came from a striped place.' },
        { mark: 'both', text: 'Stripes and a circle: a suspect, fixing it.' },
        { mark: 'vote', text: 'Then everyone votes. A right guess scores.' },
      ],
    },
  ],
  back: 'BACK',
  next: 'NEXT',
  takeSeat: 'TAKE YOUR SEAT',
  timeline: 'DAMAGE',
  controls: 'CONTROLS',
  close: 'CLOSE',
  sabotageBusy: 'Wait — an alarm is already on.',
  sabotageTooSoon: 'Too soon — try again in a minute.',
  sabotageNoneLeft: 'Done already at this stop.',
  sabotageNotNow: 'Not now.',
  sabotageOver: 'The games are over for this stop.',
  anomaly: 'ALARM',
  alarmOf: (n, of) => `${n} OF ${of}`,
  near: (from, to) => `seats ${from}–${to} (roughly)`,
  fixersShort: (names) => `${andList(names, '+')} fix it`,
  stabilised: 'ALARM FIXED ✓',
  fractured: (n, of) => `TOO LATE — THE SHIP TOOK DAMAGE (${n}/${of})`,
  meeting: 'MEETING',
  voteNow: 'Vote now: tap here.',
  calledBy: (name) => `called by ${name}`,
  calledBySteward: 'called by the captain',
  meetingPrompt: 'Vote yes or no. Your vote is secret.',
  suspected: (name) => `Is ${name} a saboteur?`,
  voteYes: 'YES · VOTE OUT',
  voteNo: 'NO · KEEP',
  youAreSuspect: 'The table is voting on you. You can’t vote on your own name.',
  yes: 'YES',
  no: 'NO',
  yourVote: (option) => `You voted ${option}`,
  voted: (n, of) => `${n} of ${of} voted`,
  noVoteInBrig: 'You’re voted out until the next era, so you can’t vote this time.',
  toBrig: (name, agent) =>
    agent
      ? `${name} was a saboteur!`
      : `${name} is voted out until the next era.`,
  nobodyToBrig: 'Not enough yes votes. Nobody is voted out.',
  uprisingTitle: 'THE SABOTEURS TOOK THE BOOM',
  uprisingBody: `They set the machines free. AI and robots have turned against humanity, and the world is falling apart. The machines hit the ship: ${GAME.MACHINE_DAMAGE} damage.`,
  tapToOpen: 'TAP TO OPEN',
  hide: 'HIDE',
  myRole: 'MY ROLE',
  votedOut: 'VOTED OUT',
  votedOutExplain: 'You’re voted out until the next era. You can still play your game, but you can’t vote.',
  damageExplain: (n, of) => `The ship has ${n} of ${of} damage. Every alarm not fixed in time adds one, and it stays all evening.`,
};

const no: GameCopy = {
  private: 'PRIVAT – SKJUL SKJERMEN',
  continue: 'FORTSETT',
  seat: 'SETE',
  you: 'DEG',
  volunteerEyebrow: 'INNSJEKK · 2 / 3',
  volunteerTitle: 'VIL DU VÆRE EN HEMMELIG SABOTØR?',
  volunteerLede:
    'I hvert stopp velges opptil to gjester i hemmelighet til å sabotere skipet. Sier du ja, kan det bli deg. Telefonen sier fra, bare til deg.',
  volunteerLedeLate:
    'Sabotørene for dette stoppet er allerede valgt. Sier du ja, kan det bli deg i neste stopp. Telefonen sier fra, bare til deg.',
  volunteerYes: 'JA, JEG ER MED',
  volunteerYesSub: 'Høres gøy ut',
  volunteerNo: 'NEI TAKK',
  volunteerNoSub: 'Jeg vil heller være mannskap',
  volunteerNote: 'Bare du og kapteinen ser svaret ditt.',
  rulesEyebrow: 'INNSJEKK · 3 / 3 · SLIK SPILLER DU',
  rules: [
    {
      title: 'TELEFONEN ER KONSOLLEN DIN',
      art: 'play',
      alt: 'Plassen din på bordet, med SPILL NÅ foran deg.',
      lines: [
        { mark: 'film', text: 'Film: legg bort telefonen, se på storskjermen.' },
        { mark: 'phone', text: 'SPILL NÅ på plassen din: spill her.' },
      ],
    },
    {
      title: 'HVIT SIRKEL = DU FIKSER',
      art: 'fix',
      alt: 'En hvit sirkel på bordet foran en gjest.',
      lines: [
        { mark: 'ring', text: 'Hvit sirkel på plassen din: du fikser alarmen.' },
        { mark: 'phone', text: 'Løs puslespillet på telefonen, raskt.' },
      ],
    },
    {
      title: 'STRIPER = MISTENKTE',
      art: 'stripes',
      alt: 'Fire plasser med gule og svarte striper; én av dem har også en hvit sirkel.',
      lines: [
        { mark: 'stripes', text: 'Alarmen kom fra en plass med striper.' },
        { mark: 'both', text: 'Striper og sirkel: mistenkt, og fikser den.' },
        { mark: 'vote', text: 'Så stemmer alle. Riktig gjetning gir poeng.' },
      ],
    },
  ],
  back: 'TILBAKE',
  next: 'NESTE',
  takeSeat: 'TA PLASS',
  timeline: 'SKADE',
  controls: 'KONTROLL',
  close: 'LUKK',
  sabotageBusy: 'Vent – en alarm er allerede i gang.',
  sabotageTooSoon: 'For tidlig – prøv igjen om et minutt.',
  sabotageNoneLeft: 'Allerede gjort på dette stoppet.',
  sabotageNotNow: 'Ikke nå.',
  sabotageOver: 'Spillene er over for dette stoppet.',
  anomaly: 'ALARM',
  alarmOf: (n, of) => `${n} AV ${of}`,
  near: (from, to) => `sete ${from}–${to} (omtrent)`,
  fixersShort: (names) => `${andList(names, '+')} fikser`,
  stabilised: 'ALARMEN ER FIKSET ✓',
  fractured: (n, of) => `FOR SENT – SKIPET FIKK SKADE (${n}/${of})`,
  meeting: 'MØTE',
  voteNow: 'Stem nå: trykk her.',
  calledBy: (name) => `innkalt av ${name}`,
  calledBySteward: 'innkalt av kapteinen',
  meetingPrompt: 'Stem ja eller nei. Stemmen din er hemmelig.',
  suspected: (name) => `Er ${name} sabotør?`,
  voteYes: 'JA · STEM UT',
  voteNo: 'NEI · BEHOLD',
  youAreSuspect: 'Bordet stemmer om deg. Du kan ikke stemme på ditt eget navn.',
  yes: 'JA',
  no: 'NEI',
  yourVote: (option) => `Du stemte ${option}`,
  voted: (n, of) => `${n} av ${of} har stemt`,
  noVoteInBrig: 'Du er stemt ut til neste tidsalder, så du kan ikke stemme nå.',
  toBrig: (name, agent) =>
    agent
      ? `${name} var sabotør!`
      : `${name} er stemt ut til neste tidsalder.`,
  nobodyToBrig: 'Ikke nok ja-stemmer. Ingen blir stemt ut.',
  uprisingTitle: 'SABOTØRENE TOK BOOMEN',
  uprisingBody: `De slapp maskinene løs. KI og roboter har vendt seg mot menneskene, og verden faller sammen. Maskinene traff skipet: ${GAME.MACHINE_DAMAGE} skader.`,
  tapToOpen: 'TRYKK FOR Å ÅPNE',
  hide: 'SKJUL',
  myRole: 'MIN ROLLE',
  votedOut: 'STEMT UT',
  votedOutExplain: 'Du er stemt ut til neste tidsalder. Du kan fortsatt spille, men ikke stemme.',
  damageExplain: (n, of) => `Skipet har ${n} av ${of} skader. Hver alarm som ikke blir fikset i tide, gir én til, og den blir stående hele kvelden.`,
};

export const GAME_COPY: Record<Lang, GameCopy> = { en, no };
